import { ErrorState, Input, useApi, useI18n, type ChildSummaryResponse, type FieldControlProps } from '@daghfal/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type KeyboardEvent } from 'react';

/** As many matches as are worth reading: past that, another word of the name is quicker than scrolling. */
const MATCHES = 10;

/** How long typing pauses before the roll is asked, so a name typed at speed is one request, not one a key. */
const PAUSE_MS = 250;

const nameOf = (child: ChildSummaryResponse) => `${child.preferredName ?? child.firstName} ${child.lastName}`;

export interface ChildPickerProps {
  /** The ids `Field` hands its control, so the field's label names this input. */
  field: FieldControlProps;
  value: ChildSummaryResponse | null;
  onChange: (child: ChildSummaryResponse | null) => void;
}

/**
 * One active child, found by name or a guardian's number as it is typed (GAPS E26).
 *
 * A list of every child cannot hold a nursery larger than the API's page, so the roll is asked for the few that
 * match instead. A combobox: the arrows move through the matches, Enter takes one, Escape closes them, and
 * typing again lets the chosen child go.
 */
export function ChildPicker({ field, value, onChange }: ChildPickerProps) {
  const api = useApi();
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = `${field.id}-matches`;

  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), PAUSE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const searching = value === null && term !== '';
  const matches = useQuery({
    queryKey: ['children', { status: 'Active', search: term, pageSize: MATCHES }],
    queryFn: () => api.children.list({ status: 'Active', search: term, pageSize: MATCHES }),
    enabled: searching,
    placeholderData: keepPreviousData,
  });

  const items = matches.data?.items ?? [];
  const showing = open && searching && matches.data !== undefined;
  const more = (matches.data?.totalCount ?? 0) - items.length;
  // The matches of an earlier search stay on screen until this one's arrive (`keepPreviousData`), and a longer
  // list may have left the highlight past the end of a shorter one.
  const current = term === text.trim() && !matches.isPlaceholderData;
  const at = active < items.length ? active : 0;
  const highlighted = showing ? items[at] : undefined;

  const choose = (child: ChildSummaryResponse) => {
    onChange(child);
    setText('');
    setTerm('');
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (items.length > 0) {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((at) => (at + step + items.length) % items.length);
      }
    } else if (event.key === 'Enter' && showing) {
      // The form is not sent: Enter here takes the highlighted child, and only one the text typed now found, so
      // a payment is never put against a match of what was typed before.
      event.preventDefault();
      if (current && highlighted) choose(highlighted);
    } else if (event.key === 'Escape' && showing) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="dg-combobox">
      <Input {...field}
        type="search"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showing}
        aria-controls={showing ? listId : undefined}
        aria-activedescendant={highlighted ? `${listId}-${highlighted.id}` : undefined}
        placeholder={t('childPicker.placeholder')}
        value={value ? nameOf(value) : text}
        onChange={(event) => {
          if (value) onChange(null);
          setText(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      />

      {showing && items.length > 0 && (
        <ul className="dg-combobox__list" id={listId} role="listbox" aria-label={t('childPicker.matches')}>
          {items.map((child, index) => (
            <li key={child.id}
              id={`${listId}-${child.id}`}
              role="option"
              aria-selected={index === at}
              className="dg-combobox__option"
              // Pressed without taking the focus, so the input's blur does not close the list under the press.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(child)}
            >
              <span>{nameOf(child)}</span>
              {child.className && <span className="dg-combobox__aside">{child.className}</span>}
            </li>
          ))}
        </ul>
      )}

      {showing && (
        <p className="dg-field__hint" role="status">
          {items.length === 0 ? t('childPicker.none') : more > 0 ? t('childPicker.more', { count: more }) : null}
        </p>
      )}
      {matches.isError && <ErrorState error={matches.error} />}
    </div>
  );
}
